import * as Sentry from "@sentry/browser";
/**
 * Checks if the error is a string or an object to return
 * the corresponding error message or a generic error
 * otherwise. Real JS exceptions (objects) are captured in Sentry;
 * backend string errors are not, as they are expected and user-facing.
 * @param {any} err
 */
export function parseCatchErr(err, t) {
  if (typeof err == "string") {
    return err;
  } else if (typeof err == "object") {
    Sentry.captureException(err);
    return err.message;
  } else {
    return t("common.Error");
  }
}

/**
 * Parses an error string into a structured object with `field` and `message`.
 *
 * The expected format is:
 *   "[field]: [message]"
 *
 * - If the string contains a colon (`:`), the part before the first colon
 *   is treated as the field name, and everything after is the message.
 * - If no colon is present, `field` will be `null` and the entire string
 *   will be the message.
 * - Leading/trailing whitespace around the field and message is trimmed.
 *
 * @param {string} errorStr - The raw error string to parse.
 * @returns {{ field: string|null, message: string }} An object representing
 *   the parsed error, with `field` either set to the field name or `null`,
 *   and `message` always set to the error message.
 *
 * @example
 * parseError("username: must not be empty");
 * { field: "username", message: "must not be empty" }
 *
 * @example
 * parseError("Something went wrong");
 * { field: null, message: "Something went wrong" }
 */
export function parseErrorFields(errorStr) {
  if (typeof errorStr !== "string") {
    return { field: null, message: String(errorStr) };
  }

  const parts = errorStr.split(":");

  if (parts.length > 1) {
    const field = parts.shift().trim();
    const message = parts.join(":").trim();
    return { field: field || null, message };
  }

  // no field provided
  return { field: null, message: errorStr.trim() };
}
